import React from 'react'
import styled, { keyframes } from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'

const fade = keyframes`from { opacity: 0 } to { opacity: 1 }`
const pop = keyframes`from { opacity: 0; transform: scale(0.98) } to { opacity: 1; transform: none }`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 18, 41, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 32px;
  animation: ${fade} 0.15s ease;
`
const Card = styled.div`
  width: 920px;
  max-width: 100%;
  height: 100%;
  max-height: 860px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(12, 18, 41, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${pop} 0.18s cubic-bezier(0.2, 0.8, 0.2, 1);
`
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'};
`
const Title = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Action = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme?.colors?.linen || '#fbede9'};
  color: ${({ theme }) => theme?.colors?.primary || '#D24724'};
  border: none;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
`
const IconBtn = styled.button`
  display: inline-flex;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#888'};
  padding: 6px;
  border-radius: 8px;
  &:hover { background: ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'}; }
`
const Body = styled.div`
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme?.colors?.lighterGrey || '#fcfdfe'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
`
const Frame = styled.iframe`width: 100%; height: 100%; border: none;`
const Img = styled.img`max-width: 100%; max-height: 100%; object-fit: contain;`
const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
  text-align: center;
  padding: 40px;
  p { margin: 0; font-size: 14px; }
`

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']

const extOf = (file) => {
  const name = file.displayName || file.name || ''
  return name.split('.').pop().toLowerCase()
}

export default function PortfolioFilePreviewModal({ file, onClose }) {
  const url = file.url || file.latestVersion?.url
  const ext = extOf(file)
  const isImage = IMAGE_EXT.includes(ext)
  const isPdf = ext === 'pdf'

  return (
    <Overlay onMouseDown={onClose}>
      <Card onMouseDown={(e) => e.stopPropagation()}>
        <Head>
          <Title>{file.displayName || file.name}</Title>
          {url && (
            <Action href={url} target="_blank" rel="noreferrer">
              <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Download
            </Action>
          )}
          <IconBtn onClick={onClose} title="Close"><CloseIcon style={{ fontSize: 20 }} /></IconBtn>
        </Head>
        <Body>
          {url && isImage && <Img src={url} alt={file.displayName || file.name} />}
          {url && isPdf && <Frame title={file.displayName || file.name} src={url} />}
          {(!url || (!isImage && !isPdf)) && (
            <Fallback>
              <DescriptionOutlinedIcon style={{ fontSize: 48, color: '#dcdfe6' }} />
              <p>
                {url
                  ? "This file type can't be previewed here."
                  : 'Preview is not available for this file.'}
              </p>
              {url && (
                <Action href={url} target="_blank" rel="noreferrer">
                  <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Download to view
                </Action>
              )}
            </Fallback>
          )}
        </Body>
      </Card>
    </Overlay>
  )
}
